/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';
import type { FluxibleContext } from 'fluxible';
import type { JSXElement } from 'jscodeshift/src/core';
import PropTypes from 'prop-types';
import { compose } from 'underscore';
import FluxibleComponent from 'vendor/cnpm/fluxible.v0-4/addons/FluxibleComponent';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import Retracked from 'js/lib/retracked';

import { Grid, PageNotification, breakpoints } from '@coursera/cds-core';

import { loadSessionsForCourse, loadSessionsForUnprivileged } from 'bundles/discussions/actions/SessionFilterActions';
import app from 'bundles/discussions/app';
import SearchResults from 'bundles/discussions/components/personalization-Search/SearchResults';
import ThreadsList from 'bundles/discussions/components/personalization-discussionsBody/ThreadsList';
import { defaults } from 'bundles/discussions/constants';

import _t from 'i18n!nls/discussions';

import ForumThread from '../forumsV2-ForumThreads';
import NewThreadButton from '../personalization-NewThreadButton';
import { SearchBox } from '../personalization-Search';

type Props = {
  className: string;

  // Routing parameters - can be overwritten from router
  questionId: string;
  answerId: string;
  commentId: string;
  sort: string;
  answered?: boolean;
  page: number;
  detailSort: string;
  detailPage: number;
  listSort: string;
  listAnswered?: boolean;
  listPage: number;
  q: string;
  searchPage: number;
  fluxibleContext: FluxibleContext;

  role?: string;
  // Flags for setting up stores that child components will depend on
  courseId: string;
  currentSessionId: string;
  hasModerationRole: boolean;
  isPrivateSession: boolean;
  isSessionsFilterStoreSetup: boolean;
  router: InjectedRouter;
};

const styles = {
  contentContainer: css`
    gap: var(--cds-spacing-200);
  `,
  searchBoxContainer: css`
    ${breakpoints.up('xs')} {
      width: 330px;
      min-width: 330px;
    }
  `,
  newThreadButtonContainer: css`
    ${breakpoints.up('sm')} {
      padding: 0;
    }
    ${breakpoints.up('xs')} {
      width: 100px;
      min-width: 100px;
    }
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) 0 0 0;
    }
  `,
  childrenContainer: css`
    max-width: 650px;
    padding: var(--cds-spacing-400) 0 0 0;
  `,
};

const SearchBarCoreLayout: React.FC = ({ children }) => {
  return (
    <Grid container>
      <Grid container item css={styles.contentContainer}>
        <Grid item xs={12} sm={5} css={styles.searchBoxContainer}>
          <SearchBox
            previousElementId="sortOrder"
            nextElementId="new-thread-btn"
            animateFromSearch={() => null} // using the searchBox interface but not needing animations
            animateToSearch={() => null} // using the searchBox interface but not needing animations
            onSubmit={() => null}
          />
        </Grid>
        <Grid item xs={12} sm={3} css={styles.newThreadButtonContainer}>
          <NewThreadButton />
        </Grid>
      </Grid>

      <Grid container item css={styles.childrenContainer}>
        {children}
      </Grid>
    </Grid>
  );
};

class DiscussionsBody extends React.Component<Props> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    fluxibleContext: PropTypes.object,
    executeAction: PropTypes.func.isRequired,
  };

  static childContextTypes = {
    fluxibleContext: PropTypes.object,
  };

  static defaultProps = {
    detailSort: defaults.detailSort,
    detailPage: defaults.detailPage,
    listSort: defaults.listSort,
    listAnswered: defaults.listAnswered,
    listPage: defaults.listPage,
  };

  threadsList?: JSXElement;

  threadDetail?: JSXElement;

  searchResults?: JSXElement;

  getChildContext() {
    return {
      fluxibleContext: this.context.fluxibleContext || this.props.fluxibleContext,
    };
  }

  constructor(props: $TSFixMe, context: $TSFixMe) {
    super(props, context);
    this.handleDismissSuccessAlert = this.handleDismissSuccessAlert.bind(this);

    /**
     * If sessionfilter store has not been loaded from a parent component then
     *  Load Session data so that SessionFilterStore is setup correctly for
     *  all thread related components (ThreadsList,ThreadDetail, SearchResults)
     */

    if (!props.isSessionsFilterStoreSetup && props.hasModerationRole) {
      context.executeAction(loadSessionsForCourse, {
        courseId: props.courseId,
        currentSessionId: props.currentSessionId,
        isPrivateSession: props.isPrivateSession,
      });
    }

    if (!props.isSessionsFilterStoreSetup && !props.hasModerationRole) {
      context.executeAction(loadSessionsForUnprivileged, {
        courseId: props.courseId,
        currentSessionId: props.currentSessionId,
        isPrivateSession: props.isPrivateSession,
      });
    }
  }

  handleDismissSuccessAlert() {
    const { router } = this.props;
    const currentQuery = router.location.query;
    const query = { ...currentQuery };
    delete query?.newThread;

    router.replace({
      ...router.location,
      query,
    });
  }

  renderSearchResults() {
    const { searchPage, q } = this.props;
    return (
      <SearchBarCoreLayout>
        <SearchResults
          query={q}
          page={searchPage}
          currentForumUrl={this.context.currentForumUrl}
          currentForum={this.context.currentForum}
          pathname={this.context.pathName}
        />
      </SearchBarCoreLayout>
    );
  }

  renderThreadsList() {
    const { hasModerationRole, listPage, listSort, listAnswered } = this.props;
    return (
      <SearchBarCoreLayout>
        <ThreadsList
          ref={(input: $TSFixMe) => {
            this.threadsList = input;
          }}
          hasModerationRole={hasModerationRole}
          sort={listSort}
          answered={listAnswered}
          page={listPage}
        />
      </SearchBarCoreLayout>
    );
  }

  renderThreadDetail() {
    const { courseId } = this.props;

    const { questionId, answerId, commentId, detailPage, detailSort, router } = this.props;
    const isNewThread = !!router.location.query?.newThread;

    return (
      <Grid container>
        {isNewThread && (
          <Grid item xs={12}>
            <PageNotification role="alert" severity="success" onDismiss={this.handleDismissSuccessAlert}>
              {_t('Your post has been created.')}
            </PageNotification>
          </Grid>
        )}

        <Grid item xs={12}>
          <ForumThread
            answerId={answerId}
            commentId={commentId}
            questionId={questionId}
            courseId={courseId}
            forumQuestionId={questionId}
            sort={detailSort}
            page={detailPage}
          />
        </Grid>
      </Grid>
    );
  }

  render() {
    const searchQuery = this.props.q;
    const { role, questionId } = this.props;

    return (
      <div
        className={classNames('rc-personalization_DiscussionsBody', 'styleguide', this.props.className)}
        id="discussions-tabpanel"
        aria-label={_t('Discussions')}
        {...(role ? { role } : {})}
      >
        {questionId && this.renderThreadDetail()}
        {!questionId && (searchQuery ? this.renderSearchResults() : this.renderThreadsList())}
      </div>
    );
  }
}

function booleanOrUndefinedFromString(value: string): boolean | undefined {
  if (/true/i.test(value)) {
    return true;
  }

  if (/false/i.test(value)) {
    return false;
  }

  return undefined;
}

const getPropsFromRouter = (router: $TSFixMe, props: { questionId: string; answerId: string; commentId: string }) => {
  const params = router.params;
  const query = router.location.query;

  const questionId = params.question_id || props.questionId;
  const page = query.page ? parseInt(query.page, 10) : 1;

  const newProps = {
    answerId: params.answer_id || props.answerId,
    questionId,
    commentId: params.comment_id || props.commentId,
    sort: query.sort,
    answered: booleanOrUndefinedFromString(query.answered),
    page,
  };

  if (questionId) {
    Object.assign(newProps, {
      detailSort: query.sort,
      detailAnswered: query.answered,
      detailPage: page,
    });
  } else if (query.q) {
    Object.assign(newProps, {
      // TODO: NOTE This might be weird ... revisit whether or not this q goes out of sync.
      q: query.q,
      searchPage: page,
    });
  } else {
    Object.assign(newProps, {
      listSort: query.sort,
      listAnswered: query.answered,
      listPage: page,
    });
  }

  return newProps;
};

const ConnectedDiscussionsBody = compose(
  connectToStores(
    ['SessionFilterStore', 'SessionStore', 'CourseStore', 'CourseMembershipStore', 'ApplicationStore'],
    ({ SessionFilterStore, SessionStore, CourseStore, CourseMembershipStore, ApplicationStore }) => {
      return {
        hasModerationRole: CourseMembershipStore.hasModerationRole() || ApplicationStore.isSuperuser(),
        isSessionsFilterStoreSetup: SessionFilterStore.filtersLoaded,
        currentSessionId: SessionStore.isEnrolled() ? SessionStore.getSessionId() : undefined,
        courseId: CourseStore.getCourseId(),
        courseSlug: CourseStore.getCourseSlug(),
        isPrivateSession: SessionStore.isEnrolled() ? SessionStore.isSessionPrivate() : false,
      };
    }
  ),
  connectToRouter(getPropsFromRouter),
  connectToRouter((router) => ({ router })),
  Retracked.createTrackedContainer((props: { sort: string; page: string; answered?: boolean }) => {
    return {
      sort: props.sort,
      answered: props.answered,
      page: props.page,
    };
  })
)(DiscussionsBody);

type ExternalProps = {
  className?: string;
  id?: string;
};

export default class FluxibleConnectedDiscussionsBody extends React.Component<ExternalProps> {
  static contextTypes = {
    fluxibleContext: PropTypes.object,
  };

  componentWillMount() {
    const { fluxibleContext } = this.context;
    this.fluxibleContext = app(fluxibleContext);
  }

  fluxibleContext: FluxibleContext | undefined = undefined;

  render() {
    return (
      <FluxibleComponent context={this.fluxibleContext?.getComponentContext()}>
        <ConnectedDiscussionsBody {...this.props} />
      </FluxibleComponent>
    );
  }
}
